if (!customElements.get('pdp-description')) {
  /**
   * PDP description that, when it runs past its CSS max-height (4 lines on mobile, 3 on desktop), is cut at the
   * last whole word that still fits with the Read More button inline after it. The button restores the full text.
   */
  class PdpDescription extends HTMLElement {
    async connectedCallback() {
      // Kept as fields: cutting the text detaches the button, so a later querySelector would not find it
      this.textEl = this.querySelector('.lp-pdp__description-text');
      this.toggleEl = this.querySelector('.lp-pdp__description-toggle');
      if (!this.textEl || !this.toggleEl) return;

      this.fullHtml = this.textEl.innerHTML;
      this.onToggleClick = this.onToggleClick.bind(this);
      this.toggleEl.addEventListener('click', this.onToggleClick);

      // Only a width change re-wraps the text; cutting it changes the height, so this cannot loop
      this.lastWidth = 0;
      this.resizeObserver = new ResizeObserver(([entry]) => {
        if (entry.contentRect.width === this.lastWidth) return;
        this.lastWidth = entry.contentRect.width;
        this.truncate();
      });
      this.resizeObserver.observe(this);

      // Web fonts can change how many lines the text needs
      await document.fonts?.ready;
      this.truncate();
    }

    disconnectedCallback() {
      this.toggleEl?.removeEventListener('click', this.onToggleClick);
      this.resizeObserver?.disconnect();
    }

    /** Binary-searches the last word after which text + button still fit the clamp. */
    truncate() {
      if (this.classList.contains('lp-pdp__description--expanded')) return;

      this.textEl.innerHTML = this.fullHtml;
      this.toggleEl.hidden = true;
      const limit = parseFloat(getComputedStyle(this.textEl).maxHeight) + 1;
      if (this.textEl.scrollHeight <= limit) return;

      const wordEnds = Array.from(this.textEl.textContent.matchAll(/\S+/g), (match) => match.index + match[0].length);
      this.toggleEl.hidden = false;

      let low = 0;
      let high = wordEnds.length - 1;
      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        this.cutAt(wordEnds[mid]);
        if (this.textEl.scrollHeight <= limit) low = mid;
        else high = mid - 1;
      }
      this.cutAt(wordEnds[low]);
    }

    /**
     * Keeps the first `length` characters of the description (formatting intact) and puts the button after them.
     * @param {number} length
     */
    cutAt(length) {
      this.textEl.innerHTML = this.fullHtml;
      const walker = document.createTreeWalker(this.textEl, NodeFilter.SHOW_TEXT);
      let remaining = length;

      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (remaining > node.length) {
          remaining -= node.length;
          continue;
        }

        const range = document.createRange();
        range.setStart(node, remaining);
        range.setEnd(this.textEl, this.textEl.childNodes.length);
        range.deleteContents();
        // After the cut, not range.insertNode: the range collapses outside the paragraph, which drops the button to its own line
        node.after(' ', this.toggleEl);
        return;
      }
    }

    onToggleClick() {
      this.toggleEl.setAttribute('aria-expanded', 'true');
      this.classList.add('lp-pdp__description--expanded');
      this.resizeObserver.disconnect();
      this.textEl.innerHTML = this.fullHtml;
      // The button left with the cut text, so move focus to the text it revealed instead of dropping it on <body>
      this.textEl.focus();
    }
  }

  customElements.define('pdp-description', PdpDescription);
}
